import { useState } from 'react';
import download from 'downloadjs';
import { Button, Label, toast } from '@osago/ui';
import dayjs from 'dayjs';
import { printDocumentService } from '../services/printDocumentService';
import { mortgageDocumentService } from '../services/mortgageDocumentService';
import { renewalDraftService } from '../services/renewalDraftService';
import { useSessionId } from '../hooks/useSessionId';
import { InsurerPublic } from '../types/api/responses';
import Icon from './icon';
import { CLIENT_URL } from '../config/api';
import { ymEvent } from '../hooks/useYandexMetrika';
import { YandexMetrikaEvent } from '../types/metrica/YandexMetrikaEvent';
import { PRODUCT_LABELS, ProductType } from '../helpers/products';
import { getCompanyLogo, getPartnerCompanyTitle } from '../helpers/companyAliases';
import { getAgreementCompanyCode, getInsurerFullName, getMortgageData } from '../helpers/mortgage';
import { Agreement } from '../types/models/agreement';
import { ApiResult } from '../types/api/common';
import {
  getDocumentName,
  getPolicyNumber,
  selectPolicyDocument,
  selectPolicyFiles,
  type PolicyFile,
} from '../helpers/policyDocument';
import { PrintDocumentResponse } from '../types/api/responses';

type AgreementItemProps = Agreement & {
  insurer?: InsurerPublic;
  /** По этому ТС уже есть более новый полис — кнопку продления не показываем */
  hasNewerPolicy?: boolean;
};

const AgreementItem = ({
  id,
  number,
  started,
  ended,
  price,
  company,
  type,
  policyType,
  dataObject,
  isMock,
  insurer,
  hasNewerPolicy,
}: AgreementItemProps) => {
  const sessionId = useSessionId();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isProlonging, setIsProlonging] = useState(false);
  const [policyFiles, setPolicyFiles] = useState<PolicyFile[] | null>(null);

  const mortgageData = getMortgageData({ company, policyType, dataObject });
  const companyCode = getAgreementCompanyCode({ company, policyType, dataObject });

  const productName = policyType
    ? (PRODUCT_LABELS[policyType as ProductType] ?? policyType.toUpperCase())
    : (type?.toUpperCase() === 'KASKO' || type?.toUpperCase() === 'КАСКО' ? 'КАСКО' : 'ОСАГО');

  const printDocument = (result: ApiResult<PrintDocumentResponse>) => {
    const selected = selectPolicyDocument(result);

    if (selected.status === 'requestFailed') {
      toast.error('Не удалось скачать полис. Попробуйте еще раз позже.');
      return;
    }

    if (selected.status === 'noDocuments') {
      toast.error('Страховая компания не предоставила файл полиса. Попробуйте еще раз позднее.');
      return;
    }

    if (selected.status !== 'ok') {
      toast.error('Не удалось подготовить PDF полиса. Попробуйте позже.');
      return;
    }

    const policyDocument = selected.document;

    download(
      `data:application/pdf;base64,${policyDocument.content}`,
      getDocumentName(productName, getPolicyNumber(policyDocument.number, number, id)),
      policyDocument.type,
    );

    toast.success('Полис скачан');
  };

  const printMortgageDocuments = (result: ApiResult<PrintDocumentResponse>) => {
    const selected = selectPolicyFiles(result);

    if (selected.status === 'requestFailed') {
      toast.error('Не удалось скачать полис. Попробуйте еще раз позже.');
      return;
    }

    if (selected.status === 'noDocuments') {
      toast.error('Страховая компания не предоставила файл полиса. Попробуйте еще раз позднее.');
      return;
    }

    if (selected.status !== 'ok') {
      toast.error('Не удалось подготовить файл полиса. Попробуйте позже.');
      return;
    }

    if (selected.files.length > 1) {
      setPolicyFiles(selected.files);
      return;
    }

    downloadPolicyFile(selected.files[0]);
    toast.success('Полис скачан');
  };

  const downloadPolicyFile = ({ document, mime, extension }: PolicyFile, index?: number) => {
    download(
      `data:${mime};base64,${document.content}`,
      getDocumentName(productName, getPolicyNumber(document.number, number, id), { extension, index }),
      mime,
    );
  };

  const onDownloadClick = () => {
    if (isDownloading) return;

    ymEvent(YandexMetrikaEvent.LK_CABINET_DOCS_CLICK_DOWNLOAD);
    setIsDownloading(true);

    const request = mortgageData
      ? mortgageDocumentService({ sessionId, agreementId: id }).then(printMortgageDocuments)
      : printDocumentService({ sessionId, company, agreementId: id }).then(printDocument);

    request.finally(() => setIsDownloading(false));
  };

  const onProlongClick = async () => {
    if (isProlonging) return;

    ymEvent(YandexMetrikaEvent.LK_CABINET_DOCS_CLICK_RENEW);

    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error('Не удалось открыть форму расчёта. Разрешите всплывающие окна и попробуйте ещё раз.');
      return;
    }
    tab.opener = null;

    setIsProlonging(true);

    try {
      const [err, res] = await renewalDraftService({
        sessionId,
        company,
        agreementId: id,
      });

      const uuid = !err && !res?.error ? res?.data?.uuid : undefined;
      tab.location.href = uuid
        ? `${CLIENT_URL}/osago?id=${uuid}&prolong=true`
        : `${CLIENT_URL}/osago?prolong=true`;
    } catch {
      tab.close();
      toast.error('Не удалось открыть форму расчёта. Попробуйте ещё раз.');
    } finally {
      setIsProlonging(false);
    }
  };

  const companyName = insurer?.title || getPartnerCompanyTitle(companyCode) || companyCode;

  const carName = dataObject?.brandName
    ? `${dataObject.brandName} ${dataObject.modelName || '—'}`
    : '—';

  const driversList = dataObject
    ? dataObject.driversRestriction === false
      ? 'Без ограничений'
      : dataObject.driverList?.map((d) => d.fullName).join(', ') || '—'
    : '—';

  const periodStart = started ? dayjs(started).format('DD.MM.YYYY') : '—';
  const periodEnd = ended ? dayjs(ended).format('DD.MM.YYYY') : '—';

  const today = dayjs().startOf('day');
  const startDate = dayjs(started).startOf('day');
  const isUpcoming = startDate.isAfter(today, 'day');

  const PROLONG_WINDOW_DAYS = 60;
  const endDate = ended ? dayjs(ended) : null;

  const isOsago = policyType
    ? policyType === 'osago'
    : type?.toUpperCase() !== 'KASKO' && type?.toUpperCase() !== 'КАСКО';
  const canProlong = Boolean(
    isOsago && endDate && endDate.diff(today, 'day') <= PROLONG_WINDOW_DAYS && !hasNewerPolicy,
  );

  const getUpcomingLabel = (): string => {
    const totalDays = startDate.diff(today, 'day');
    const months = startDate.diff(today, 'month');
    if (months >= 1) {
      const rem = months % 10;
      const tens = months % 100;
      let word = 'месяцев';
      if (rem === 1 && tens !== 11) word = 'месяц';
      else if (rem >= 2 && rem <= 4 && (tens < 12 || tens > 14)) word = 'месяца';
      return `Начнется через ${months} ${word}`;
    }
    const rem = totalDays % 10;
    const tens = totalDays % 100;
    let word = 'дней';
    if (rem === 1 && tens !== 11) word = 'день';
    else if (rem >= 2 && rem <= 4 && (tens < 12 || tens > 14)) word = 'дня';
    return `Начнется через ${totalDays} ${word}`;
  };

  return (
    <div className="policy-card">
      <div className="policy-card__header">
        <div className="policy-card__company">
          <img
            src={getCompanyLogo(companyCode)}
            alt={companyName}
            className="policy-card__logo"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <div className="policy-card__company_header">
            <div className="policy-card__product">{productName}</div>
            <span
              className="policy-card__company-name"
              title={insurer?.description || undefined}
            >
              {companyName}
            </span>
          </div>
        </div>
        {isUpcoming && <span className="policy-card__upcoming-badge">{getUpcomingLabel()}</span>}
        {isMock && <span className="policy-card__mock-badge">Тестовый</span>}
        <div className="policy-card__actions">
          {canProlong && (
            <Button
              variant="primary"
              size="sm"
              className="policy-card__action-btn"
              leftIcon={<Icon name="renew" width={14} height={14} className="fill-current" />}
              isLoading={isProlonging}
              onClick={onProlongClick}
            >
              Продлить полис
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            className="policy-card__action-btn"
            leftIcon={<Icon name="paperclip" width={14} height={14} className="fill-current" />}
            isLoading={isDownloading}
            onClick={onDownloadClick}
          >
            Скачать полис
          </Button>
        </div>
      </div>

      {policyFiles && (
        <div className="flex flex-wrap items-center gap-[8px] mb-[16px]">
          <Label variant="caption">Полис состоит из нескольких файлов:</Label>
          {policyFiles.map((file, index) => (
            <Button
              key={index}
              variant="ghost"
              size="sm"
              onClick={() => downloadPolicyFile(file, index + 1)}
            >
              {`Файл ${index + 1} (${file.extension})`}
            </Button>
          ))}
        </div>
      )}

      <div className="policy-card__body">
        {mortgageData ? (
          <>
            <div className="policy-card__field">
              <Label variant="caption">Страхователь</Label>
              <span className="policy-card__value">{getInsurerFullName(mortgageData)}</span>
            </div>
            <div className="policy-card__field">
              <Label variant="caption">Банк</Label>
              <span className="policy-card__value">{mortgageData.bank || '—'}</span>
            </div>
            <div className="policy-card__field">
              <Label variant="caption">Период страхования</Label>
              <span className="policy-card__value">{periodStart} - {periodEnd}</span>
            </div>
            <div className="policy-card__field">
              <Label variant="caption">Номер полиса</Label>
              <span className="policy-card__value">{mortgageData.policyNumber || number || id}</span>
            </div>
            <div className="policy-card__field">
              <Label variant="caption">Стоимость полиса</Label>
              <span className="policy-card__value">{price} ₽</span>
            </div>
          </>
        ) : (
          <>
            <div className="policy-card__field">
              <Label variant="caption">Застраховано</Label>
              <span className="policy-card__value">{carName}</span>
            </div>
            <div className="policy-card__field">
              <Label variant="caption">Период страхования</Label>
              <span className="policy-card__value">{periodStart} - {periodEnd}</span>
            </div>
            <div className="policy-card__field">
              <Label variant="caption">Водители</Label>
              <span className="policy-card__value">{driversList}</span>
            </div>
            <div className="policy-card__field">
              <Label variant="caption">Стоимость полиса</Label>
              <span className="policy-card__value">{price} ₽</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AgreementItem;
